import { Observable } from 'rxjs';

export enum PhotoStatuses {
  PENDING_REVIEW = 'pending_review',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  DELETED = 'deleted',
}

export interface ReviewPayload {
  request_note: string;
}

export interface Task {
  approval_required: boolean | null;
  created: string;
  flags: number | null;
  id: number | null;
  modified: string;
  name: string;
  position: number | null;
  settle_on_approve: boolean;
  status: number | null;
  type: number | null;
}

export interface PhotoFile {
  deleted: null | string;
  exam_photo_id: number;
  filename: string;
  id: number;
  id_type: null | string;
  notes: null | string;
  rejection_note: string;
  reviewed: string;
  reviewed_by: number;
  status: PhotoStatuses;
  image_uri?: string;
  timestamp?: Data | string;
}

export interface Photo {
  approved: boolean;
  deleted: null | boolean | string;
  camera_side: string;
  file: PhotoFile;
  id: number;
  more: {
    text: string;
    title: string;
  };
  optional: boolean;
  text: string;
  type: string;
  notes: string | null;
  rejected: boolean | string;
  canBeDeleted?: boolean;
  image_uri: string | null;
  rotation?: number;
  aiDermInfo?: AiDermResponse & { error?: string };
  class?: string;
}

export interface IdPhotoResponse {
  id_types: IdTypes[];
  photos: Photo[];
  diagnosis: string | null;
  count?: Counts;
}

export interface ExamPhotoResponse {
  photos: Photo[];
  diagnosis: string | null;
}

export interface ExamPhotoPayload {
  reviews: ExamReview[];
}

export interface ExamReview {
  approved: boolean;
  file_id: number;
}

export type IdTypes = undefined | 'Passport' | 'Driving license' | 'Citizen card' | 'Other';

export interface Data {
  id_types: IdTypes;
  photos: Photo[];
}

export interface ErrorMessages {
  400: string;
  403: string;
}

export interface Counts {
  approved_request_pair: number;
  remaining_request_pair: number;
  request_last_sent: Date | null;
  id_and_selfie_required: number;
  id_and_selfie_unapproved: number;
}

export interface AdditionalIdResponse {
  photos: Photo[];
  id_types: IdTypes;
  count: Counts;
  diagnosis: string | null;
}

export interface DataResponse {
  photos: Photo[];
  diagnosis: string | null;
  id_types: IdTypes[];
}

export interface ExamPhotoDataResponse {
  photos: Photo[];
  diagnosis: string | null;
}

export interface MappedData {
  photos$: Observable<Photo[]>;
  diagnosis$: Observable<string | null>;
  types$: Observable<IdTypes[]>;
}

export interface ExamPhotoMappedData {
  photos$: Observable<Photo[]>;
  diagnosis$: Observable<string | null>;
}

export interface PropsPayload {
  payload: { photos: Photo[] };
}

export interface Review {
  file_id: number;
  approved: boolean;
  id_type: null | string;
  rejection_note?: string | undefined;
}

export interface UpdatePhotoPayload {
  reviews: Review[];
}

export interface RequestAdditionalIdMessage {
  request_note: string;
}

export interface RequestAdditionalExamPhoto {
  request_note: string;
  number: number;
}

export interface VisitDetailPhotoState {
  diagnosis: string | null;
  examPhotos: Photo[];
  loadingAiDermMap: Record<number, boolean>;
}

export interface AiDermPostRequest {
  file_id: number;
  image: string;
  filename: string;
  type: string;
}

export interface AiDermPrediction {
  confidence: number;
  icd_10?: string;
  disease?: string;
  read_more_url?: string;
  icd?: string;
  possibility?: string;
  readMoreUrl?: string;
  name: string;
}

export interface AiDermResponse {
  file_id: number;
  created?: number;
  predictions?: AiDermPrediction[];
}

export interface AiDermCouldntDetectSkinError {
  code: number; // 200
  error_message: string;
  status: string; // 'OK'
}

export interface PhotoFileAlreadySavedBeSide {
  file_id: number;
  created: number;
  created_by: string;
}

export interface MedicalPhotoPayload {
  file: {};
}

export interface MedicalDocumentFile {
  link: string;
  created: string;
  filename: string;
}

export interface MedicalDocumentsResponse {
  credits_left: number;
  files: MedicalDocumentFile[];
}
